import {
  createFileRoute,
  redirect,
  stripSearchParams,
} from "@tanstack/react-router";
import { PageFailed } from "@/client/components/route-states";
import { TeamPeople, TeamPeoplePending } from "@/client/components/team-people";
import { pageTitle } from "@/client/lib/page-title";
import { PENDING_MS } from "@/client/lib/pending";
import { ensureTenantData } from "@/client/lib/session";
import { tasksQuery } from "@/client/lib/tasks-query";
import { useNow } from "@/client/lib/use-now";
import { peopleQuery } from "@/features/people/people-query";
import { runsTeam } from "@/features/people/roles";
import {
  PEOPLE_SEARCH_DEFAULTS,
  peopleSearch,
} from "@/features/people/schemas";

const TICK = 60_000;

export const Route = createFileRoute("/_authenticated/$slug/team/people")({
  head: async ({ match }) => {
    const { peopleCopy } = await import("@/features/people/copy");
    return {
      meta: [
        {
          title: pageTitle(
            `${peopleCopy.tabs.people} · ${peopleCopy.title}`,
            match.context.companyName,
          ),
        },
      ],
    };
  },
  validateSearch: peopleSearch,
  // Only a search typed into the box is written into a link.
  search: { middlewares: [stripSearchParams(PEOPLE_SEARCH_DEFAULTS)] },
  // An Employee's Team is already the list.
  beforeLoad: ({ context, params }) => {
    if (!runsTeam(context.role)) {
      throw redirect({ to: "/$slug/team", params });
    }
  },
  loader: async ({ context }) => {
    await Promise.all([
      ensureTenantData(context.queryClient, peopleQuery),
      ensureTenantData(context.queryClient, tasksQuery),
    ]);
  },
  pendingMs: PENDING_MS,
  pendingComponent: PeoplePending,
  errorComponent: PageFailed,
  component: People,
});

function PeoplePending() {
  return <TeamPeoplePending q={Route.useSearch().q} />;
}

function People() {
  const { q } = Route.useSearch();
  const navigate = Route.useNavigate();
  const now = useNow(TICK);
  return (
    <TeamPeople
      now={now}
      q={q}
      // Replaced so Back leaves the tab instead of stepping through every keystroke.
      onSearch={(next) => navigate({ search: { q: next }, replace: true })}
    />
  );
}
